import moment from 'moment';

// `amount: null` = no lower bound (all time).
export const DATE_RANGE_OPTIONS = [
  { value: '24h', label: '24h', amount: 24, unit: 'hours' },
  { value: '7d', label: '7d', amount: 7, unit: 'days' },
  { value: '30d', label: '30d', amount: 30, unit: 'days' },
  { value: '90d', label: '90d', amount: 90, unit: 'days' },
  { value: 'all', label: 'All', amount: null, unit: null }
];

export const DEFAULT_DATE_RANGE = '7d';

export const getDateRangeParams = dateRange => {
  const option = DATE_RANGE_OPTIONS.find(o => o.value === dateRange) || DATE_RANGE_OPTIONS.find(o => o.value === DEFAULT_DATE_RANGE);
  if (option.amount == null) return {};
  return { from: moment().subtract(option.amount, option.unit).toISOString() };
};

export const AGENT_ACTION_COPY = {
  dismiss: 'Dismiss',
  new_product: 'New product',
  new_variant: 'Match existing product',
  sibling: 'Match existing product',
  ambiguous: 'Abstained (close candidates)',
  unknown: 'Unknown',
  triage_blocked: 'Skipped (triage)',
  no_agent_result: "Agent didn't run"
};

export const REVIEW_TAG_LABELS = {
  wrong_product: 'Wrong product',
  missed_existing_product: 'Missed existing product',
  wrong_brand: 'Wrong brand',
  wrong_category: 'Wrong category',
  wrong_image: 'Wrong image',
  bad_product_name: 'Bad product name',
  variant_confusion: 'Variant confusion',
  preowned_or_marketplace: 'Pre-owned / marketplace',
  insufficient_evidence: 'Needs more evidence',
  crawler_evidence_bad: 'Crawler evidence bad',
  agent_should_dismiss: 'Agent should dismiss',
  human_truth_unclear: "Human's match unclear",
  human_truth_wrong: 'Human was wrong',
  high_confidence_bad: 'Confident but wrong',
  good_automation_candidate: 'Good automation candidate'
};

export const formatPercent = value => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Math.round(Number(value) * 1000) / 10}%`;
};

export const formatDate = value => (value ? moment(value).format('MMM D, h:mm a') : '—');

export const formatNumber = value => Number(value || 0).toLocaleString();

export const readable = value => {
  if (value == null || value === '') return '—';
  return String(value).replace(/_/g, ' ');
};

export const isTrue = value => value === true || value === 1 || value === 'true' || value === '1';
const isFalse = value => value === false || value === 0 || value === 'false' || value === '0';

export const getReviewTagLabel = tag => REVIEW_TAG_LABELS[tag] || readable(tag);

const getRawAgentVerdict = evaluation => {
  const runDecision = evaluation?.agentRunJson?.agentResults?.match?.decision || {};
  return evaluation?.agentMatchVerdict || evaluation?.matchVerdict || evaluation?.agentAction || runDecision.verdict || null;
};

const getAgentDecisionProductId = evaluation => {
  const runDecision = evaluation?.agentRunJson?.agentResults?.match?.decision || {};
  return evaluation?.agentMatched_Product_id || runDecision.Product_id || runDecision.parentProductId || null;
};

const isAgentTriageBlocked = evaluation => {
  const triage = evaluation?.triage || evaluation?.agentRunJson?.triage || evaluation?.agentEvidenceJson?.triage || {};
  return isFalse(triage.shouldProceed) || (!getRawAgentVerdict(evaluation) && evaluation?.tier === 'ESCALATION');
};

export const getAgentDecisionSummary = evaluation => {
  const verdict = getRawAgentVerdict(evaluation);
  const Product_id = getAgentDecisionProductId(evaluation);
  const product = evaluation?.agentProduct || null;
  const triage = evaluation?.triage || evaluation?.agentRunJson?.triage || {};
  const base = {
    raw: verdict,
    Product_id,
    productTitle: product?.title || evaluation?.agentProductName || null,
    product
  };
  if (isAgentTriageBlocked(evaluation)) {
    return {
      ...base,
      action: 'triage_blocked',
      label: AGENT_ACTION_COPY.triage_blocked,
      detail: readable(triage.reason || evaluation?.domainBlockedReason || 'no catalog action')
    };
  }
  if (verdict === 'dismiss')
    return { ...base, action: 'dismiss_url', label: AGENT_ACTION_COPY.dismiss, detail: 'Agent would remove this URL from matching.' };
  if (verdict === 'new_product')
    return { ...base, action: 'create_product', label: AGENT_ACTION_COPY.new_product, detail: 'Agent proposed creating a catalog product.' };
  if (verdict === 'new_variant')
    return {
      ...base,
      action: 'attach_existing_product',
      label: AGENT_ACTION_COPY.new_variant,
      detail: Product_id
        ? `Raw agent verdict was variant; counted as match existing product #${Product_id} for review.`
        : 'Raw agent verdict was variant, but no parent product id was stored.'
    };
  if (verdict === 'sibling')
    return {
      ...base,
      action: 'attach_existing_product',
      label: AGENT_ACTION_COPY.sibling,
      detail: Product_id ? `Matched existing catalog product #${Product_id}.` : 'No product id stored.'
    };
  if (verdict === 'ambiguous') {
    const closeIds = evaluation?.agentRunJson?.agentResults?.match?.decision?.closeProductIds || [];
    return {
      ...base,
      action: 'ambiguous_review',
      label: AGENT_ACTION_COPY.ambiguous,
      detail: closeIds.length
        ? `Agent could not separate ${closeIds.length} close candidate(s): ${closeIds.map(id => `#${id}`).join(', ')}.`
        : 'Agent abstained without a stored close set.'
    };
  }
  if (evaluation?.status && evaluation.status !== 'SUCCEEDED')
    return { ...base, action: 'no_agent_result', label: AGENT_ACTION_COPY.no_agent_result, detail: readable(evaluation.status) };
  return { ...base, action: 'unknown', label: AGENT_ACTION_COPY.unknown, detail: verdict ? readable(verdict) : 'No agent verdict stored.' };
};

export const getAgentSummary = evaluation => {
  const run = evaluation?.agentRunJson || {};
  const matchDecision = run.agentResults?.match?.decision || {};
  return {
    verdict: evaluation?.agentMatchVerdict || matchDecision.verdict || evaluation?.agentAction || null,
    Product_id: evaluation?.agentMatched_Product_id || matchDecision.Product_id || matchDecision.parentProductId || null,
    brandName: evaluation?.agentBrandName || run.agentResults?.brand?.decision?.brandName || null,
    productName: evaluation?.agentProductName || run.agentResults?.name?.decision?.productName || null,
    categoryName: evaluation?.agentCategoryName || run.agentResults?.category?.decision?.categoryName || evaluation?.agentProposedCategoryName || null,
    primaryImageUrl: evaluation?.agentPrimaryImageUrl || run.agentResults?.image?.decision?.primaryImageUrl || null
  };
};

export const getLambdaSummary = evaluation => {
  const lambda = evaluation?.lambdaFieldsJson || {};
  return {
    brandName: evaluation?.sourceBrandName || lambda.brandName || lambda.scrapedBrandName || null,
    productName: evaluation?.sourceProductName || lambda.productName || lambda.scrapedProductName || null,
    categoryName: evaluation?.sourceCategoryName || lambda.proposedCategoryName || lambda.categoryName || null,
    primaryImageUrl: evaluation?.sourcePrimaryImageUrl || lambda.primaryImageUrl || null,
    Product_id: lambda.Product_id || lambda.matched_Product_id || null
  };
};
